/**
 * What a rep is told when the checks refused every draft and none is shown
 * (docs/2026-10-05-withheld-card.md).
 *
 * The run decides to withhold; this only reads what it recorded. The client is given the ids of
 * the checks that refused the last draft (`draft.withheld`) and one `draft.checked` per check of
 * each attempt. So a check is named here in words written for a rep. Beside the events, since
 * Dawid's answer of 15:47 CEST 2026-10-09 (ledger `abd7ff5a`), it is also given the span each check
 * refused in the last draft, read from the saved run (`refused-spans.ts`), and nothing else of that
 * draft (docs/2026-10-09-held-card-quote-and-slot-length.md).
 */
import { z } from "zod";
import { isContextKey } from "../../../skills/leadcue-reply/scripts/lib/context-contracts.ts";
import type { StoredConversationEvent } from "./conversation-events.ts";
import { FACT_NAMES } from "./fact-names.ts";

/**
 * What each check refuses, said of the draft it refused. One entry per blocking rule in the
 * registry (`rules/index.ts`); the test fails for a blocking rule with no entry, so a new check
 * cannot reach a rep as its id or as nothing.
 */
const CHECK_WORDS: Readonly<Record<string, string>> = {
  no_follow_up_after_no: "This campaign does not follow up after a no.",
  campaign_prose_step: "It invited the lead to a step this campaign does not allow.",
  campaign_next_step_audit:
    "Its next step, or its claim that something was done, did not match this campaign.",
  campaign_next_step: "The next step it chose is not one this campaign allows.",
  campaign_action_scope: "It proposed an action outside what this campaign allows.",
  duplicate_reply_prefix: "Its subject repeated the reply prefix.",
  invitation_promise_in_copy: "It promised an invitation that has not been sent.",
  outer_whitespace: "It had stray space at its start or end.",
  sign_off_detected: "It wrote its own sign-off instead of leaving the campaign’s.",
  signature_block_detected:
    "It wrote its own contact details instead of leaving the campaign’s sign-off.",
  dash_detected: "It used a dash the house style does not allow.",
  placeholder_detected: "It still contained a template placeholder.",
  internal_provenance_leak: "It mentioned how the draft was produced.",
  provider_metadata_leak: "It mentioned sending-tool or campaign details a lead should not see.",
  meaningful_body: "It said almost nothing beyond a greeting or a sign-off.",
  word_count_high: "It was too long.",
  recipient_identity: "Its greeting named the wrong person.",
  back_reference_unreceived: "It referred back to something this person never received.",
  redundant_self_intro: "It introduced the sender again in a thread that already knows them.",
  self_name_third_person: "It spoke about the sender by name instead of as “I”.",
  authorized_follow_up_promise: "It promised a follow-up nobody authorized.",
  unsupported_commercial_mechanism:
    "It described how the offer works in a way no saved answer backs up.",
  unapproved_delivery_timing: "It promised a delivery time nobody approved.",
  unapproved_pricing_stage: "It added a pricing step nobody confirmed.",
  limited_assurance_negated: "It turned “not guaranteed” into a flat “no”.",
  unapproved_physical_location: "It stated a location nobody confirmed.",
  unknown_fact_negated: "It answered “no” to something that is simply not known.",
  unverified_delivery_claim: "It said something was sent or booked that has not been.",
  proposed_action_only: "It described a proposed action as already done.",
  blanket_flexibility: "It promised open-ended availability.",
  closed_world_evidence_ids:
    "It cited a source that is not in this conversation or its saved answers.",
  unverified_held_call: "It said a call took place, which nothing confirms.",
  false_silence_claim: "It said the lead had not replied, when they had.",
  reply_guard_copy: "It repeated wording that is saved as something to keep internal.",
  forbidden_literal_claim: "It used a claim this campaign forbids.",
  unapproved_url: "It included a link that is not one of yours.",
  booking_page_offered: "It arranged a call without offering your booking page.",
  seller_resource_secondary: "It left out material this campaign sends with the first reply.",
  casual_marker_guard: "It was too casual for this campaign’s formal tone.",
  deferred_client_questions: "It answered a question that should have been left for the call.",
  slot_weekday_label: "It gave the wrong weekday for a time slot.",
  recipient_timezone_dependency: "It depends on the lead’s time zone, which is not confirmed.",
  past_time_confirmed: "It confirmed a day that has already passed.",
  late_reply_acknowledged: "It did not own the delay in answering.",
  earlier_campaign_background:
    "It treated the lead’s earlier conversation as more than background.",
  semantic_audit_integrity: "The check that reads a draft against its sources could not verify it.",
  must_not_imply: "It implied something the reply must not say.",
  unplanned_disclosure: "It disclosed saved information the plan for this reply did not call for.",
  claim_authority: "It stated something no saved answer or email in this thread backs up.",
  material_commitments:
    "It made a commitment nobody approved, such as a guarantee, a discount or a deadline.",
  product_knowledge: "It described the product or its pricing beyond what is approved.",
  sparse_context_inflation: "It invented steps or benefits from very little information.",
  question_coverage: "It left one of the lead’s questions unanswered.",
  commitment_continuity: "It ignored an open promise or the next step the lead asked for.",
  history_and_temporal: "It got the conversation’s history or timing wrong.",
  recycled_proof_point: "It repeated a sales point instead of answering directly.",
  register_and_respect: "Its tone did not match the lead’s, or did not respect their position.",
  prose_quality: "It was not written clearly and naturally enough.",
  thread_fit: "It did not read as the next message in this thread.",
};

const CONTEXT_RULE_PREFIX = "context_";

/** A check's words for a rep, or null for a check nobody has written words for. */
export function checkWords(ruleId: string): string | null {
  const own = CHECK_WORDS[ruleId];
  if (own !== undefined) return own;
  if (!ruleId.startsWith(CONTEXT_RULE_PREFIX)) return null;
  const fact = ruleId.slice(CONTEXT_RULE_PREFIX.length);
  return isContextKey(fact)
    ? `A fact it needed was missing or unclear: ${FACT_NAMES[fact]}.`
    : null;
}

/** Longer than this is more than the sentence objected to, and is not shown. */
export const MAX_SPAN_CHARACTERS = 280;

/** One span of the refused draft and the check that refused it, as the events route sends it. */
export const RefusedSpanSchema = z.strictObject({
  ruleId: z.string().min(1).max(400),
  quote: z.string().min(1).max(MAX_SPAN_CHARACTERS),
});
export type RefusedSpan = z.infer<typeof RefusedSpanSchema>;

/** A refusing check in the rep's words, and the spans of the draft it refused. */
export type WithheldReason = {
  readonly text: string;
  /** Quoted from the refused draft, never sent and never offered as reply text. */
  readonly refused: readonly string[];
};

export type WithheldView = {
  /**
   * `refused`: checks refused the draft and are named. `stopped`: the run withheld it and named no
   * check. `failed`: drafting itself failed part way.
   */
  readonly kind: "refused" | "stopped" | "failed";
  /** How many drafts the run checked, from its own record; null when it wrote none or none is known. */
  readonly attempts: number | null;
  /** The refusing checks in the rep's words, in the order the run recorded them. */
  readonly reasons: readonly WithheldReason[];
  /** Refusing checks with no words. They are counted and never shown by id. */
  readonly unnamed: number;
};

/** A check made of the facts before any draft exists; it is not an attempt at a draft. */
const BEFORE_A_DRAFT = "context:";

export function withheldView(
  events: readonly StoredConversationEvent[],
  spans: readonly RefusedSpan[] = [],
): WithheldView {
  const stop = events
    .filter((event) => event.kind === "draft.withheld")
    .reduce<(StoredConversationEvent & { kind: "draft.withheld" }) | null>(
      (latest, event) => (latest === null || event.id > latest.id ? event : latest),
      null,
    );
  if (stop === null) return { kind: "stopped", attempts: null, reasons: [], unnamed: 0 };

  const ruleIds = [...new Set(stop.detail.ruleIds)];
  const unnamed = ruleIds.filter((ruleId) => checkWords(ruleId) === null).length;
  // One line per sentence of words: two checks a rep reads the same way share a line and its spans.
  const reasons: WithheldReason[] = [];
  for (const ruleId of ruleIds) {
    const text = checkWords(ruleId);
    if (text === null) continue;
    const refused = spans.filter((span) => span.ruleId === ruleId).map((span) => span.quote);
    const line = reasons.findIndex((reason) => reason.text === text);
    if (line === -1) reasons.push({ text, refused });
    else {
      const kept = reasons[line];
      if (kept) reasons[line] = { text, refused: [...new Set([...kept.refused, ...refused])] };
    }
  }
  const drafts = new Set(
    events
      .filter((event) => event.kind === "draft.checked")
      .filter((event) => event.evidence.kind === "run" && event.evidence.ref === stop.evidence.ref)
      .filter((event) => !event.detail.verdict.startsWith(BEFORE_A_DRAFT))
      .map((event) => event.detail.revision),
  ).size;
  const failed = stop.detail.state === "stage_failed";
  return {
    kind: failed ? "failed" : reasons.length + unnamed > 0 ? "refused" : "stopped",
    attempts: drafts > 0 && !failed ? drafts : null,
    reasons,
    unnamed,
  };
}
